import { useMemo, useState } from 'react';
import { getCoreRowModel } from '@tanstack/react-table';

// Hooks and services
import { deleteApk, downloadApkFile, getApkInfos } from '../../../services/apk.service';

// Constants
import { AppPermissions } from '../../../constants/Permissions';

// Types
import { ApkInfo } from '../../../types/ApkInfo';

// Common Components
import Paper from '@mui/material/Paper';
import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { FileUploadOutlined } from '@mui/icons-material';

// Components
import { useNewTable, Table } from '../../../components/common/table/index';
import { makeAppReleasesColumn } from '../../../utils/columns/makeAppReleasesColumn';
import { AuthGuard } from '../../common/AuthGuard';
import { UploadApkModal } from './modals/UploadApkModal';
import { EditApkModal } from './modals/EditApkModal';
import { parseError } from '../../../utils';
import { useNotifyMessage } from '../../../hooks/useNotifyMessage';
import { tablePrimaryButtonSx } from '../../../styles/tableButtonStyles';

export const AppReleases = () => {
  // TODO:
  // 1. Fix the error message if you hit table reload
  // 2. Make sure that sorting by version number works correctly.
  //    Right now, I am unsure if it would by default.

  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [apkEditing, setApkEditing] = useState<ApkInfo | null>(null);
  const [downloadingFile, setDownloadingFile] = useState<string | null>(null);

  const forceReload = () => setParamFilter({ ...paramFilter, changedAt: new Date() });
  const [paramFilter, setParamFilter] = useState({});

  const { notifyMessage } = useNotifyMessage();

  const handleClickDownload = async (apkInfo: ApkInfo) => {
    if (downloadingFile) return;
    try {
      setDownloadingFile(apkInfo.fileName);
      await downloadApkFile(apkInfo.fileName);
      notifyMessage('Download complete!', 'success');
    } catch (err) {
      notifyMessage(parseError(err), 'error');
    } finally {
      setDownloadingFile(null);
    }
  };

  const handleClickDelete = async (fileName: string) => {
    try {
      if (confirm('Are you sure you want to delete this file?')) await deleteApk(fileName);
      forceReload();
    } catch (err) {
      notifyMessage(parseError(err), 'error');
    }
  };

  const handleClickEdit = (apkInfo: ApkInfo) => {
    setApkEditing(apkInfo);
  };

  const columns = useMemo(() => makeAppReleasesColumn(handleClickDownload, handleClickEdit, handleClickDelete, downloadingFile), [downloadingFile]);

  const table = useNewTable({
    columns,
    fetchAsync: getApkInfos,
    serverSide: true,
    shouldFetch: true,
    params: paramFilter,
  });

  return (
    <>
      <Paper sx={{ p: 1.5 }}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: { xs: 'flex-start', md: 'center' },
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            rowGap: 1,
            columnGap: 1.5,
            mb: 1.5,
            px: { xs: 0.5, sm: 0.75 },
          }}
        >
          <Typography variant='h6' sx={{ m: 0, fontWeight: 700, color: '#1f2d63' }}>
            Download Mobile App Releases
          </Typography>

          <AuthGuard permission={AppPermissions.Apk.Edit}>
            <Button
              variant='contained'
              size='small'
              onClick={() => setIsUploadModalOpen(true)}
              endIcon={<FileUploadOutlined />}
              sx={tablePrimaryButtonSx}
            >
              Upload Release
            </Button>
          </AuthGuard>
        </Box>

        {downloadingFile && (
          <Stack sx={{ px: { xs: 0.5, sm: 0.75 }, pb: 1 }}>
            <Alert severity='info' icon={<CircularProgress size={16} color='inherit' />} sx={{ py: 0.25, borderRadius: 2 }}>
              Download in progress...
            </Alert>
          </Stack>
        )}

        <Table {...table} getCoreRowModel={getCoreRowModel()} />
      </Paper>

      {/* Upload modal */}
      {isUploadModalOpen && <UploadApkModal setModalOpen={setIsUploadModalOpen} forceReloadTable={forceReload} />}

      {/* Edit modal */}
      {apkEditing && (
        <EditApkModal
          apkInfo={apkEditing}
          setModalOpen={(open) => {
            if (!open) setApkEditing(null);
          }}
          forceReloadTable={forceReload}
        />
      )}
    </>
  );
};
